/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo } from 'react';
import {
  Grid, Fab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useConfirm } from 'material-ui-confirm';
import { useForm, useFormState } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';
import ParticipantPromptGuest from './ParticipantPromptGuest';

export default function ParticipantPrompt({
  invite,
  params: {
    participantCategoryGroupings = null,
    noEmail = false,
    noAttendeeCountChangeAfterRegistration = false,
    noAttendeeCreation = false,
    maxCount,
    attributes,
    participantDescription,
    attendeeEmailLabel,
    helperTexts,
    showTicketCategory,
    upgradeButtons,
    upgradeBenefits,
    customFieldDefinition,
  },
  submitType,
  inviteForm,
}) {
  const confirm = useConfirm();
  const form = useForm();
  const { values } = useFormState();
  const maxQuotaCount = useMemo(() => {
    const formId = values.customAttributes?.switchForm?.replace('formSwitch:', '');
    if (!formId) return null;
    if (!invite.quotas) return null;
    const quota = invite.quotas.find((quota) => quota.formId === parseInt(formId, 10));
    if (!quota) return null;
    return quota.maxCount;
  }, [values.customAttributes?.switchForm, invite.quotas]);

  const noAttendeeCountChange = (noAttendeeCountChangeAfterRegistration && submitType === 'update') || inviteForm.type === 'updateExistingTickets';
  const currentAttendeeCount = values.attendees.length;
  const effectiveMaxCount = useMemo(() => {
    const limits = [maxCount, invite?.maxCustomerCount].filter((limit) => Number.isFinite(limit));
    if (!limits.length) return null;
    return Math.min(...limits);
  }, [maxCount, invite?.maxCustomerCount]);
  const attendeeLimitReached = effectiveMaxCount && currentAttendeeCount >= effectiveMaxCount;

  const handleAdd = async (grouping, values) => {
    let waitingList = false;

    if (attendeeLimitReached) {
      await confirm({
        title: 'Maximale Anzahl erreicht',
        description: `Sie haben die maximale Anzahl von ${effectiveMaxCount} Gästen erreicht! Es können keine weiteren Gäste hinzugefügt werden.`,
        hideCancelButton: true,
        confirmationText: 'Zurück',
      });
      return;
    }

    if (maxQuotaCount && (values.attendees.length + 2) > maxQuotaCount && !values.waitingList && !values.eventTables?.[0]?.id) {
      await confirm({
        title: 'Warteliste',
        description: `
          Aufgrund der limitierten Plätze können Sie maximal ${(maxQuotaCount - 1) === 1 ? 'ein zusätzlichen Gast' : `${maxQuotaCount - 1} zusätzliche Gäste`} registrieren.
          Sie haben die Möglichkeit, sich mit all Ihren Gästen auf die Warteliste setzen zu lassen oder keine weiteren Gäste mehr hinzuzufügen.
        `,
        confirmationText: 'Mich und meine Gäste auf die Warteliste setzen',
        cancellationText: 'Nicht hinzufügen',
      });
      form.change('waitingList', true);
      waitingList = true;
    }

    const uuid = uuidv4();
    const newAttendees = [
      ...values.attendees,
      {
        tempId: uuid,
        forename: '',
        name: '',
        email: '',
        customAttributes: {},
        waitingList,
        ticketCategory: { id: grouping?.ticketCategoryId },
      },
    ];
    form.change('attendees', newAttendees);
  };

  useEffect(() => {
    const attendees = [...values.attendees];
    if (!attendees.length
      && invite?.customAttributes?.forenameCompanion
      && invite?.customAttributes?.nameCompanion) {
      attendees.push({
        tempId: uuidv4(),
        title: invite.customAttributes.titleCompanion,
        forename: invite.customAttributes.forenameCompanion,
        name: invite.customAttributes.nameCompanion,
        email: invite.email,
        ticketCategory: { id: participantCategoryGroupings[0].ticketCategoryId },
        // formOptions: { disabled: true },
      });
      form.change('attendees', attendees);
    }
  }, []);

  const handleDelete = async (index, grouping, values) => {
    const attendee = values.attendees[index];
    try {
      if (attendee.forename || attendee.name) {
        await confirm({
          title: `${grouping?.attendeeLabel || 'Gast'} löschen`,
          description: `Wollen Sie wirklich ${attendee.forename} ${attendee.name} löschen? Dieser Vorgang ist unwiderruflich.`,
          confirmationText: 'Löschen',
          cancellationText: 'Abbrechen',
          confirmationButtonProps: { color: 'error' },
        });
      }
      let newAttendees = [...values.attendees];
      if (attendee.tempId) {
        newAttendees = newAttendees.filter(({ tempId, companionTempId }) => tempId !== attendee.tempId && companionTempId !== attendee.tempId);
      }

      if (attendee.id) {
        newAttendees = newAttendees.filter(({ id }) => id !== attendee.id);
      }

      form.change('attendees', newAttendees);

      if (values.seating.length) {
        for (const [table, seats] of Object.entries(values.seating)) {
          const indexInSeating = seats.indexOf(index);
          if (indexInSeating === -1) continue;
          delete values.seating[table][indexInSeating];
        }
      }
      // eslint-disable-next-line no-empty
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    if (values.attendees.length) return;
    handleAdd(null, values);
  }, []);

  const renderAttendees = (values) => {
    const attendees = values.attendees.map((attendee, index) => { attendee.index = index; return attendee; });

    return (
      <>
        <ParticipantPromptGuest
          attendees={attendees}
          handleDelete={handleDelete}
          startParticipantNumber={0}
          noEmail={noEmail}
          participantDescription={participantDescription}
          attributes={attributes}
          inviteForm={inviteForm}
          noAttendeeCountChange={noAttendeeCountChange}
          attendeeEmailLabel={attendeeEmailLabel}
          helperTexts={helperTexts}
          showTicketCategory={showTicketCategory}
          upgradeButtons={upgradeButtons}
          upgradeBenefits={upgradeBenefits}
          customFieldDefinition={customFieldDefinition}
        />
        {!noAttendeeCountChange && !noAttendeeCreation && (
          <Grid container direction="row" justifyContent="center" style={{ marginTop: '40px', marginBottom: '10px' }}>
            <Grid item>
              <Fab size="medium" variant="extended" color={attendeeLimitReached ? undefined : 'primary'} aria-label="add" onClick={() => handleAdd(null, values)}>
                <AddIcon sx={{ mr: 1 }} />
                {participantDescription}
                {' '}
                hinzufügen
              </Fab>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      {renderAttendees(values)}
    </>
  );
}
